import React, { useState } from 'react'

import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Switch from 'components_new/atoms/Switch'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

import EntitySlices from 'components_new/organisms/EntitySlices'

import { useTranslation } from 'language/translator'

import { KpiOptionObject } from 'types/GlobalKpiOption'
import { TSlice } from 'types/GlobalSlice'
import { isCustomEconomyMetric } from 'types/GlobalMetric'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useMetric } from 'redux/hooks/Metrics'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { TPatchKpiOptionBody } from 'redux/api/KpiOptions'

interface MetricSectionProps {
  kpi: KpiOptionObject
  onClose: () => void
  updateKpiOption: (body: TPatchKpiOptionBody, cb: () => void) => void
}

const MetricSection = (props: MetricSectionProps) => {
  const { kpi, onClose, updateKpiOption } = props
  const { t, lang } = useTranslation()

  /*-- drawer --*/
  const [drawer, setDrawer] = useState(false)

  // using loading during patch to avoid any unwanted behavior
  const [sliceLoading, setSliceLoading] = useState(false)
  const [slicesDialog, setSlicesDialog] = useState<{
    open: boolean
    configureSliceIds?: string[]
  }>({ open: false })

  const { isAdmin } = useAccountSettings()

  const handleBackDrawer = () => {
    setDrawer(false)
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleUpdateSlices = (slices: string[]) => {
    setSliceLoading(true)

    updateKpiOption({ slices }, () => setSliceLoading(false))
  }

  const metric = useMetric(kpi.kpi_template_id, kpi.variant_kpi_template_id)

  if (!metric) {
    return null
  }

  const selectedSliceIds = kpi.slices ?? []

  const unconfiguredSelectedSlices = metric.slices.disabled.filter((item) =>
    selectedSliceIds.includes(item.id)
  )

  const selectedSlices = kpi.slices
    .map((sliceId) => metric.slices.all.find((item) => item.id === sliceId))
    .filter((item): item is TSlice => Boolean(item))

  const title = isCustomEconomyMetric(metric)
    ? metric.full_title[lang]
    : metric.title[lang]

  return (
    <>
      <SettingsGroup title={t('widgets', 'metric')}>
        <SettingsItem
          disabled={true}
          onClick={() => {}}
          title={t('widgets', 'metric')}
          value={title}
          variant="edit"
        />
        <SettingsItem
          onClick={() => setDrawer(true)}
          title={t('entities', 'slices')}
          value={
            selectedSlices.length > 0
              ? selectedSlices
                .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
                .map((slice) => slice.name)
                .join(', ')
              : t('global', 'none')
          }
        />
      </SettingsGroup>

      {/*-- drawer --*/}
      <SettingsDrawer
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        open={drawer}
        title={t('entities', 'slices')}
      >
        {unconfiguredSelectedSlices.length > 0 ? (
          <SettingsGroup
            title={t('entities', 'used_not_configured')}
            helperText={t('entities', 'used_not_configured_helper_text')}
          >
            {unconfiguredSelectedSlices.map((slice) => (
              <SettingsItem
                key={slice.id}
                title={slice.name}
                actions={
                  <>
                    <IconButton
                      onClick={() =>
                        setSlicesDialog({
                          open: true,
                          configureSliceIds: [slice.id]
                        })
                      }
                      size="small"
                    >
                      <Icon fontSize="small" name="Edit" />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        handleUpdateSlices(
                          kpi.slices.filter((sliceId) => sliceId !== slice.id)
                        )
                      }}
                      size="small"
                    >
                      <Icon fontSize="small" name="Delete" />
                    </IconButton>
                  </>
                }
              />
            ))}
          </SettingsGroup>
        ) : null}
        <SettingsGroup
          title={t('entities', 'available_slices')}
          helperText={t('entities', 'metric_slice_helper_text')}
        >
          {metric.slices.available.length > 0 ? (
            metric.slices.available.map((slice) => {
              const isSelected = !!kpi.slices.find(
                (sliceId) => sliceId === slice.id
              )

              const singleRequiredMode =
                metric.slices.selection_mode === 'single_required'

              const isSupported =
                slice.supported_fact_table_ids?.includes(
                  metric.fact_table_id
                ) ?? true

              // an unsupported slice that is already selected stays
              // interactive so that it can be switched off
              const isUnsupported = !isSupported && !isSelected

              return (
                <SettingsItem
                  key={slice.id}
                  title={slice.name}
                  disabled={isUnsupported}
                  disabledText={t('entities', 'slice_not_supported_by_metric')}
                  actions={
                    <Switch
                      checked={isSelected}
                      disabled={
                        sliceLoading ||
                        (singleRequiredMode && isSelected) ||
                        isUnsupported
                      }
                      onChange={() => {
                        const slices = kpi.slices

                        let newSlices: string[]

                        if (singleRequiredMode) {
                          if (isSelected) return

                          newSlices = [slice.id]
                        } else if (isSelected) {
                          newSlices = slices.filter(
                            (sliceId) => sliceId !== slice.id
                          )
                        } else {
                          newSlices = [...slices, slice.id]
                        }

                        handleUpdateSlices(newSlices)
                      }}
                      size="small"
                    />
                  }
                />
              )
            })
          ) : (
            <SettingsItem>
              <EmptyPlaceholder
                background={true}
                title={t('entities', 'no_slices_yet')}
              />
            </SettingsItem>
          )}
          {isAdmin && metric.entity_id ? (
            <SettingsItem
              color="success"
              onClick={() => setSlicesDialog({ open: true })}
              title={t('entities', 'edit_slices')}
              variant="add"
            />
          ) : null}
        </SettingsGroup>

        {metric.entity_id ? (
          <SimpleDialog
            actions={
              <Button
                onClick={() => setSlicesDialog({ open: false })}
                variant="text"
              >
                {t('global', 'close')}
              </Button>
            }
            onClose={() => setSlicesDialog({ open: false })}
            open={slicesDialog.open}
            sx={{
              zIndex: 1302 // for fullSize
            }}
            title={t('entities', 'slices')}
          >
            <EntitySlices
              edit
              id={metric.entity_id}
              configureSpecificSlices={slicesDialog.configureSliceIds}
            />
          </SimpleDialog>
        ) : null}
      </SettingsDrawer>
    </>
  )
}

export default MetricSection
